// Page-specific styles for the PhantomSlipPanel. Layout, status pills, and
// per-selection rows. Shared card / cardEyebrow / cardTitle / cardBody live
// in dashboard.styles.ts.
//
// Phase 9C.10b: local `metaTag / metaTagAccent / metaTagNeutral` exports
// removed — consumers now use shared `pillAmberOutline / pillNeutral`
// from `src/styles/status-pills.ts`. Card elevation + transition spread
// via the new sibling `phantom-slip-panel-rails.styles.ts`.

import type { CSSProperties } from "react";

export const metaRow: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "var(--sz-space-2)",
};

export const summaryRow: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: "var(--sz-space-3)",
  alignItems: "baseline",
  borderBottom: "1px solid var(--sz-border-subtle)",
  paddingBottom: "8px",
};

export const summaryLabel: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-secondary)",
};

export const summaryValue: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-md)",
  letterSpacing: "1.5px",
  color: "var(--sz-text-primary)",
};

export const fixtureList: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-3)",
};

export const fixtureCard: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  padding: "14px 16px",
  background: "var(--sz-bg-surface-2)",
  border: "1px solid var(--sz-border-default)",
  borderRadius: "var(--sz-radius-md)",
};

export const fixtureHeader: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: "var(--sz-space-3)",
  alignItems: "baseline",
  flexWrap: "wrap",
};

export const fixtureTeams: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-md)",
  color: "var(--sz-text-primary)",
  fontWeight: 500,
};

export const fixtureLeague: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  letterSpacing: "1.5px",
  color: "var(--sz-text-muted)",
};

export const fixtureMeta: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "var(--sz-space-3)",
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-secondary)",
};

export const fixtureMetaItem: CSSProperties = {
  display: "inline-flex",
  alignItems: "baseline",
  gap: "6px",
};

export const fixtureMetaKey: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

export const disclaimer: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
  padding: "12px 14px",
  background: "var(--sz-bg-surface-2)",
  borderLeft: "2px solid var(--sz-border-default)",
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-muted)",
  lineHeight: 1.55,
};

export const primaryBtn: CSSProperties = {
  background: "var(--sz-amber)",
  color: "var(--sz-text-inverse)",
  border: "none",
  padding: "12px 18px",
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  fontWeight: 500,
  letterSpacing: "1px",
  textTransform: "uppercase",
  borderRadius: "var(--sz-radius-md)",
  alignSelf: "flex-start",
};

export const errorBar: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-danger-text)",
  background: "var(--sz-danger-dim)",
  border: "1px solid var(--sz-danger)",
  padding: "10px 14px",
  borderRadius: "var(--sz-radius-md)",
};

export const confirmedNote: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
  padding: "12px 14px",
  background: "var(--sz-success-dim)",
  borderLeft: "2px solid var(--sz-success)",
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-secondary)",
  lineHeight: 1.55,
};

export const confirmedNoteHeading: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-success-text)",
};

// Terminal-state (post-resolve) styles. Won uses success accent;
// busted uses muted-amber (warning) accent. No fake metrics — we only
// report the simulated outcome and the bust leg label if present.

export const terminalCardWon: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
  padding: "16px 18px",
  background: "var(--sz-success-dim)",
  borderLeft: "2px solid var(--sz-success)",
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-secondary)",
  lineHeight: 1.55,
};

export const terminalCardBusted: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
  padding: "16px 18px",
  background: "var(--sz-warning-dim)",
  borderLeft: "2px solid var(--sz-warning)",
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-secondary)",
  lineHeight: 1.55,
};

export const terminalEyebrowWon: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-success-text)",
};

export const terminalEyebrowBusted: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-warning-text)",
};

export const bustLegLine: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  letterSpacing: "1px",
  color: "var(--sz-text-muted)",
};
